import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { onAuthStateChanged, signInWithPopup, signOut as fbSignOut } from "firebase/auth";
import type { User } from "firebase/auth";
import { auth, googleProvider } from "@/config/firebase";
import { env } from "@/config/env";
import { capabilitiesFor } from "./rbac";
import type { Capabilities } from "./rbac";
import { parseClaims } from "./claims";
import type { OperatorClaims } from "./claims";

export interface AuthState {
  /** Firebase user, or null when signed out. */
  user: User | null;
  /** Parsed operator claims (roles + backendId). Null until a user is resolved. */
  claims: OperatorClaims | null;
  /** UX capability flags derived from claims + the current environment. */
  capabilities: Capabilities;
  /** True while the initial auth state is resolving. */
  loading: boolean;
  /** Last sign-in error message, if any. */
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Fetch a fresh ID token for an admin-API call (force-refresh optional). */
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
  /** Re-read custom claims from a freshly-minted token (after a role change). */
  refreshClaims: () => Promise<void>;
}

const NO_CAPS: Capabilities = {
  read: false,
  act: false,
  actDestructive: false,
  actProd: false,
  superAdmin: false,
};

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<OperatorClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadClaims = useCallback(async (u: User | null, forceRefresh = false) => {
    if (!u) {
      setClaims(null);
      return;
    }
    const token = await u.getIdTokenResult(forceRefresh);
    setClaims(parseClaims(token));
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      try {
        await loadClaims(u);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    });
  }, [loadClaims]);

  const signIn = useCallback(async () => {
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
    setClaims(null);
  }, []);

  const getIdToken = useCallback(
    async (forceRefresh = false) => {
      if (!auth.currentUser) throw new Error("Not signed in");
      return auth.currentUser.getIdToken(forceRefresh);
    },
    [],
  );

  const refreshClaims = useCallback(async () => {
    await loadClaims(auth.currentUser, true);
  }, [loadClaims]);

  const capabilities = useMemo(
    () => (claims ? capabilitiesFor(claims, env.eliosEnv) : NO_CAPS),
    [claims],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      claims,
      capabilities,
      loading,
      error,
      signIn,
      signOut,
      getIdToken,
      refreshClaims,
    }),
    [user, claims, capabilities, loading, error, signIn, signOut, getIdToken, refreshClaims],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
